import React, { useId } from 'react';
import { resolveTaxRegime } from '../../../utils/tax-regime';
import type { GstinInputProps } from './types';

// ─── Constants ────────────────────────────────────────────────────────────────

// The validation pattern, length and example are regime-driven (India GSTIN,
// UAE/KSA/Bahrain 15-digit numbers, Oman `OM` + 10 digits) via
// `resolveTaxRegime` → `@bbpos/types` `REGIME_TAX_ID_FORMAT`. India is
// byte-identical to the prior hardcoded GSTIN regex, hint and placeholder.
const GSTIN_LENGTH = 15;
const FORMAT_HINT = 'e.g., 27AAPFU0939F1ZV';
const GSTIN_PLACEHOLDER = '27AAPFU0939F1ZV';

// ─── Icons ────────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg
      className="h-4 w-4 text-success-500 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ─── GstinInput ───────────────────────────────────────────────────────────────

export function GstinInput({
  value = '',
  onChange,
  label,
  error,
  disabled = false,
  required = false,
  onValidated,
  regime,
}: GstinInputProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  // Hook-free: default to the India (INR) regime so existing call sites
  // validate GSTIN and render the `GSTIN` label byte-identically.
  const resolved = regime ?? resolveTaxRegime('INR');
  const taxIdRegex = resolved.taxIdRegex;
  const effectiveLabel = label ?? resolved.taxIdLabel ?? '';
  // India keeps its exact legacy hint/placeholder; other regimes get their own
  // (a UAE `TRN` field must not show a GSTIN example).
  const isIndia = resolved.regime === 'GST_INDIA';
  const formatHint = isIndia ? FORMAT_HINT : (resolved.taxIdHint ?? FORMAT_HINT);
  const placeholder = isIndia ? GSTIN_PLACEHOLDER : (resolved.taxIdExample ?? '');
  // Exact id length per regime (India/UAE/KSA/Bahrain 15, Oman 12). No regime
  // pattern (Kuwait/Qatar) ⇒ keep the legacy 15; callers hide the field anyway.
  const maxLength = resolved.taxIdLength ?? GSTIN_LENGTH;

  const upperValue = value.toUpperCase();
  const isFilled = upperValue.length === maxLength;
  // No regime pattern (e.g. Kuwait/Qatar) ⇒ no format constraint.
  const isValid = isFilled && (taxIdRegex ? taxIdRegex.test(upperValue) : true);
  const hasFormatError = isFilled && !isValid;

  // External error prop takes precedence; fall back to inline format error
  const displayError =
    error ?? (hasFormatError ? `Invalid ${effectiveLabel} format` : undefined);
  const hasError = Boolean(displayError);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, maxLength);
    onChange?.(raw);

    const filled = raw.length === maxLength;
    if (filled) {
      onValidated?.(taxIdRegex ? taxIdRegex.test(raw) : true);
    }
  }

  // Border / ring state
  const rowClasses = [
    'flex items-center w-full border rounded bg-white overflow-hidden transition-colors duration-150 h-11',
    'dark:bg-neutral-800 dark:text-neutral-100',
    hasError
      ? 'border-danger-500 focus-within:ring-2 focus-within:ring-danger-500 focus-within:ring-offset-0'
      : isValid
        ? 'border-success-500 focus-within:ring-2 focus-within:ring-success-500 focus-within:ring-offset-0'
        : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500 focus-within:ring-offset-0',
    disabled
      ? 'bg-neutral-100 dark:bg-neutral-700 cursor-not-allowed opacity-70 pointer-events-none'
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  const describedBy = [hasError ? errorId : '', hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1">
      {effectiveLabel && (
        <label
          htmlFor={id}
          className={[
            'text-sm font-medium leading-none',
            hasError
              ? 'text-danger-700 dark:text-danger-400'
              : 'text-neutral-700 dark:text-neutral-300',
          ].join(' ')}
        >
          {effectiveLabel}
          {required && (
            <span className="ml-0.5 text-danger-500" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      <div className={rowClasses}>
        <input
          id={id}
          type="text"
          inputMode="text"
          value={upperValue}
          onChange={handleChange}
          disabled={disabled}
          required={required}
          aria-required={required || undefined}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          placeholder={placeholder}
          maxLength={maxLength}
          autoComplete="off"
          spellCheck={false}
          className={[
            'flex-1 h-full bg-transparent outline-none border-none min-w-0 pl-3 py-2 text-sm',
            'placeholder:text-neutral-400 dark:placeholder:text-neutral-500',
            'text-neutral-900 dark:text-neutral-100 tracking-widest font-mono',
            disabled ? 'cursor-not-allowed' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        />
        {isValid && !hasError && (
          <span className="pr-3 flex items-center">
            <CheckIcon />
          </span>
        )}
      </div>

      {/* Format hint — always visible */}
      <span id={hintId} className="text-xs text-neutral-500 dark:text-neutral-400">
        {formatHint}
      </span>

      {hasError && (
        <span id={errorId} role="alert" className="text-xs text-danger-600 dark:text-danger-400">
          {displayError}
        </span>
      )}
    </div>
  );
}
